import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Spin, Table, Tag } from "antd";
import { useSelector } from "react-redux";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import { useTimeTracker } from "../TimeTrackerProvider/TimeTrackerProvider";
import {
  checkInTimeTracker,
  checkOutTimeTracker,
  fetchTimeTrackerHistory,
  pauseTimeTracker,
  resumeTimeTracker,
} from "../../../app/utils/timeTracker/timeTrackerApi";
import { showErrorResponse } from "../../../app/utils/common/Functions";
import { formatDateTimeIst, formatWorkingDuration, computeTodayTotalWorkingMinutes, nowIst, resolveSessionWorkingMinutes, todayIstDateStr } from "../../../app/utils/common/dateTimeIst";
import { TIME_TRACKER_SESSION_UPDATED_EVENT } from "../../../app/utils/constants/apiConstant";
import { requestTimeTrackerTrackingPermissions } from "../../../app/utils/timeTracker/timeTrackerPermissions";
import {
  computeLiveWorkingMinutes,
  isSessionPaused,
} from "../../../app/utils/timeTracker/timeTrackerSessionUtils";
import { showSuccessToast } from "../../../app/utils/common/Toast";
import "./TimeTrackerPage.scss";

const formatDateTime = formatDateTimeIst;

const formatDurationFromMinutes = (minutes) => formatWorkingDuration(minutes, { fromMinutes: true });

const statusColor = (status) => {
  if (status === "active") return "processing";
  if (status === "completed") return "success";
  if (status === "auto_checkout") return "gold";
  return "default";
};

const statusLabel = (status) => {
  if (status === "auto_checkout") return "Paused (ended)";
  if (status === "completed") return "completed";
  return status;
};

const TimeTrackerPage = () => {
  const user = useSelector((state) => state.auth?.auth?.user);
  const { activeSession, refreshSession, setActiveSession } = useTimeTracker();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [minuteTick, setMinuteTick] = useState(0);

  const isCheckedIn = Boolean(activeSession);
  const isPaused = isSessionPaused(activeSession);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const from = nowIst().subtract(30, "days").format("YYYY-MM-DD");
      const to = todayIstDateStr();
      const sessions = await fetchTimeTrackerHistory({ from, to, limit: 50 });
      setHistory(sessions);
    } catch (error) {
      showErrorResponse(error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await refreshSession();
      await loadHistory();
    };
    init();
  }, [refreshSession, loadHistory, user?.id]);

  useEffect(() => {
    const onSessionUpdated = () => {
      refreshSession();
      loadHistory();
    };
    window.addEventListener(TIME_TRACKER_SESSION_UPDATED_EVENT, onSessionUpdated);
    return () => window.removeEventListener(TIME_TRACKER_SESSION_UPDATED_EVENT, onSessionUpdated);
  }, [refreshSession, loadHistory]);

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      await requestTimeTrackerTrackingPermissions();
      const session = await checkInTimeTracker();
      setActiveSession(session);
      showSuccessToast("Checked in successfully.");
      await loadHistory();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      await checkOutTimeTracker({ sessionId: activeSession?.id });
      setActiveSession(null);
      showSuccessToast("Checked out successfully.");
      window.dispatchEvent(new CustomEvent(TIME_TRACKER_SESSION_UPDATED_EVENT));
      await loadHistory();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      const session = await pauseTimeTracker();
      setActiveSession(session);
      showSuccessToast("Time tracker paused.");
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const session = await resumeTimeTracker();
      setActiveSession(session);
      showSuccessToast("Time tracker resumed.");
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!isCheckedIn || isPaused) return undefined;
    const interval = setInterval(() => setMinuteTick((value) => value + 1), 60000);
    return () => clearInterval(interval);
  }, [isCheckedIn, isPaused]);

  const liveMinutes = useMemo(
    () => computeLiveWorkingMinutes(activeSession),
    [activeSession, minuteTick]
  );

  const todayTotalMinutes = useMemo(
    () =>
      computeTodayTotalWorkingMinutes(history, {
        liveMinutes,
        activeSessionId: activeSession?.id,
      }),
    [history, liveMinutes, activeSession?.id, minuteTick]
  );

  const columns = [
    {
      title: "Date",
      key: "date",
      render: (_, row) => formatDateTime(row.checkInAt),
    },
    {
      title: "Check In",
      dataIndex: "checkInAt",
      key: "checkInAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Check Out",
      dataIndex: "checkOutAt",
      key: "checkOutAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Working Hours",
      key: "hours",
      render: (_, row) => {
        const minutes = resolveSessionWorkingMinutes(row, {
          liveMinutes,
          activeSessionId: activeSession?.id,
        });
        return minutes != null ? formatDurationFromMinutes(minutes) : "—";
      },
    },
    {
      title: "Attendance",
      dataIndex: "attendanceStatus",
      key: "attendanceStatus",
      render: (value) => <Tag color="blue">{value || "present"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <Tag color={statusColor(value)}>
          {statusLabel(value)}
        </Tag>
      ),
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivityAt",
      key: "lastActivityAt",
      render: (value) => formatDateTime(value),
    },
  ];

  return (
    <div className="time-tracker-page page-container">
      <PageContainerHeader title="Time Tracker" />
      <div className="card-border card-body mb-4">
        <div className="time-tracker-page__status-grid">
          <div>
            <div className="text-sm text-gray-500">Current Status</div>
            <Tag
              color={!isCheckedIn ? "default" : isPaused ? "gold" : "green"}
              className="mt-1"
            >
              {!isCheckedIn ? "Not Checked In" : isPaused ? "Paused" : "Checked In"}
            </Tag>
          </div>
          <div>
            <div className="text-sm text-gray-500">Check In</div>
            <div className="font-medium">{formatDateTime(activeSession?.checkInAt)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Last Activity</div>
            <div className="font-medium">{formatDateTime(activeSession?.lastActivityAt)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Working Hours (Today)</div>
            <div className="font-medium">
              {formatDurationFromMinutes(todayTotalMinutes)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            type="primary"
            disabled={isCheckedIn}
            loading={actionLoading && !isCheckedIn}
            onClick={handleCheckIn}
          >
            Check In
          </Button>
          {isCheckedIn && !isPaused ? (
            <Button loading={actionLoading} onClick={handlePause}>
              Pause
            </Button>
          ) : null}
          {isCheckedIn && isPaused ? (
            <Button type="primary" loading={actionLoading} onClick={handleResume}>
              Resume
            </Button>
          ) : null}
          <Button
            danger
            disabled={!isCheckedIn}
            loading={actionLoading && isCheckedIn && !isPaused}
            onClick={handleCheckOut}
          >
            Check Out
          </Button>
        </div>
        <p className="text-sm text-gray-500 mt-3 mb-0">
          Activity uses PC idle detection in Chrome / installed app (mouse, keyboard, screen lock)
          when you Allow idle detection on Check In — so you can keep working in other apps with
          the dashboard minimized. A desktop notification appears only when the PC is idle for 5
          minutes; if you stay idle another 2 minutes, the session auto-pauses. Sessions also reset
          each day (IST). Keep the dashboard open in the background (or as an installed app) and
          allow notifications for alerts.
        </p>
      </div>

      <div className="card-border card-body">
        <h3 className="text-lg font-semibold text-sky-900 mb-3">Attendance History</h3>
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={history}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default TimeTrackerPage;
